<template>
  <div>
    <v-simple-table>
      <thead>
        <tr>
          <th>
            <v-icon>mdi-check</v-icon>
          </th>
          <th>{{ $capitalize($tc("model.roombudget_coverage")) }}</th>
          <th>{{ $capitalize($tc("model.roombudget_forfait")) }}</th>
          <th>{{ $capitalize($tc("model.roombudget_amount")) }}</th>
          <th class="text-right">{{ $capitalize($tc("model.roombudget_forfait_price")) }}</th>
          <th class="text-right">{{ $capitalize($tc("model.roombudget_material_price")) }}</th>
          <th class="text-right">{{ $capitalize($tc("model.roombudget_total")) }}</th>
          <th width="100px"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="budgets.length == 0" key="empty">
          <td class="grey--text text-center" colspan="7">
            {{ $capitalize($tc("model.roombudget_budget_product")) }}
          </td>
        </tr>
        <tr v-for="budget in budgets" :key="budget.id" class="budget-row">
          <td>
            <v-simple-checkbox
              :value="budget.selected"
              @click="handleSelectRoomBudget(!budget.selected, budget)"
              :disabled="
                !$store.getters['auth/getScope'](
                  'leads.damages.budgets.approve'
                )
              "
            ></v-simple-checkbox>
          </td>
          <td>{{ budget.coverage.type_description }}</td>
          <td>{{ budget.forfait_description }}</td>
          <td>{{ budget.quantity }} {{ budget.unit_of_measurement }}</td>
          <td class="text-right">{{ formatEuro(budget.forfait_price) }}</td>
          <td class="text-right">{{ formatEuro(budget.material_price) }}</td>
          <td class="text-right" :class="{ 'grey--text': !budget.selected }">
            {{ formatEuro(calcBudget(budget)) }}
          </td>
          <td>
            <v-btn @click="handleUpdateRoomBudget(budget)" icon small>
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn @click="confirmDeleteBudget(budget)" icon small>
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr key="total">
          <td colspan="6" class="text-right">{{ $capitalize($tc("model.roombudget_approved_total")) }}</td>
          <td class="text-right">{{ formatEuro(calcTotalBudget()) }}</td>
          <td></td>
        </tr>
      </tfoot>
    </v-simple-table>
    <v-divider></v-divider>
    <v-row>
      <v-col>
        <v-chip @click="handleOpenBudgetDialog()">
          <v-icon left>mdi-plus</v-icon>{{ $capitalize($tc("model.roombudget_register_budget")) }}
        </v-chip>
      </v-col>
    </v-row>
    <v-dialog v-model="room_dialog" max-width="600" persistent>
      <v-card>
        <v-form @submit.prevent="handleSubmitForm">
          <ValidationObserver ref="budget_form" v-slot="{ invalid }">
            <v-card-title>
              {{ $capitalize($tc("model.roombudget_fraction_budget")) }}
              <v-spacer></v-spacer>
              <v-btn @click="handleCloseRoomDialog()" icon>
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <ValidationProvider
                name="policy_coverage"
                rules="required"
                v-slot="{ errors }"
              >
                <v-select
                  :label="$capitalize($tc('model.roombudget_coverage'))"
                  :items="coverages"
                  name="policy_coverage"
                  item-value="id"
                  item-text="type_description"
                  clearable
                  :error-messages="errors"
                  v-model="budget_form.policy_coverage_id"
                  :disabled="submitting"
                ></v-select>
              </ValidationProvider>
              <v-card elevation="3" class="my-5">
                <v-card-title>{{ $capitalize($tc("model.roombudget_digital_quote")) }}</v-card-title>
                <v-card-text>
                  <ValidationProvider
                    name="forfait_description"
                    rules="required|max:100"
                    v-slot="{ errors }"
                  >
                    <v-combobox
                      v-model="budget_form.forfait_description"
                      name="forfait_description"
                      :label="$capitalize($tc('model.roombudget_forfait'))"
                      :items="forfaits"
                      item-text="description"
                      @input="test"
                      :error-messages="errors"
                      :disabled="submitting"
                    >
                      <template v-slot:item="{ item, on, attrs }">
                        <v-list-item v-bind="attrs" v-on="on">
                          <v-list-item-content>
                            <v-list-item-title>{{
                              item.description
                            }}</v-list-item-title>
                            <v-list-item-subtitle>
                              {{ item.observation }}
                            </v-list-item-subtitle>
                            <v-list-item-subtitle>
                              {{ formatEuro(item.price) }} /
                              {{ item.unit_of_measurement }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-combobox>
                  </ValidationProvider>
                  <ValidationProvider
                    name="forfait_observation"
                    rules="max:255"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="budget_form.forfait_observation"
                      :label="$capitalize($tc('model.roombudget_observation'))"
                      name="forfait_observation"
                      :error-messages="errors"
                      :disabled="submitting"
                    ></v-text-field>
                  </ValidationProvider>
                  <v-row>
                    <v-col>
                      <ValidationProvider
                        name="unit_of_measurement"
                        rules="required|max:255"
                        v-slot="{ errors }"
                      >
                        <v-text-field
                          v-model="budget_form.unit_of_measurement"
                          name="unit_of_measurement"
                          :label="$capitalize($tc('model.roombudget_measure'))"
                          :error-messages="errors"
                          :disabled="submitting"
                        ></v-text-field>
                      </ValidationProvider>
                    </v-col>
                    <v-col>
                      <ValidationProvider
                        name="quantity"
                        rules="required|min_value:0.1"
                        v-slot="{ errors }"
                      >
                        <v-text-field
                          v-model="budget_form.quantity"
                          :label="$capitalize($tc('model.roombudget_amount'))"
                          type="number"
                          name="quantity"
                          :error-messages="errors"
                          :disabled="submitting"
                        ></v-text-field>
                      </ValidationProvider>
                    </v-col>
                    <v-col>
                      <ValidationProvider
                        name="forfait_price"
                        rules="required|min_value:0"
                        v-slot="{ errors }"
                      >
                        <vuetify-money
                          v-model="budget_form.forfait_price"
                          name="forfait_price"
                          type="text"
                          :options="money"
                          :label="$capitalize($tc('model.roombudget_price_per_unit'))"
                          :error-messages="errors"
                          :disabled="submitting"
                        />
                      </ValidationProvider>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              <ValidationProvider
                name="material_price"
                rules="required|min_value:0"
                v-slot="{ errors }"
              >
                <vuetify-money
                  v-model="budget_form.material_price"
                  name="material_price"
                  type="text"
                  :options="money"
                  :label="$capitalize($tc('model.roombudget_material_price'))"
                  :error-messages="errors"
                  :disabled="submitting"
                />
              </ValidationProvider>
              <v-divider class="mt-5"></v-divider>
              <v-list-item class="mb-5">
                <v-list-item-content>
                  <v-list-item-title>{{ $capitalize($tc("model.roombudget_total")) }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action style="font-size: 20px">
                  {{ formatEuro(calcBudget(budget_form)) }}
                </v-list-item-action>
              </v-list-item>
              <v-checkbox
                v-model="budget_form.selected"
                :label="$capitalize($tc('model.roombudget_approved_budget'))"
              ></v-checkbox>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn @click="handleCloseRoomDialog()" text>{{ $capitalize($tc("model.roombudget_cancel_button")) }}</v-btn>
              <v-btn type="submit" color="primary" :loading="submitting"
                >{{ $capitalize($tc("model.roombudget_submit_button")) }}</v-btn
              >
            </v-card-actions>
          </ValidationObserver>
        </v-form>
      </v-card>
      <v-dialog v-model="forfaits_dialog" max-width="900">
        <v-card>
          <v-card-title>{{ $capitalize($tc("model.roombudget_forfaits")) }}</v-card-title>
          <v-card-text>
            <v-text-field
              :label="$capitalize($tc('model.roombudget_search'))"
              prepend-inner-icon="mdi-magnify"
              single-line
            ></v-text-field>
          </v-card-text>
          <v-simple-table style="height: 470px; overflow-x: auto">
            <thead>
              <tr>
                <th>{{ $capitalize($tc("model.roombudget_forfait")) }}</th>
                <th>{{ $capitalize($tc("model.roombudget_observation")) }}</th>
                <th>{{ $capitalize($tc("model.roombudget_measure")) }}</th>
                <th>{{ $capitalize($tc("model.roombudget_price")) }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="forfait in forfaits" :key="forfait.id">
                <td>{{ forfait.description }}</td>
                <td>{{ forfait.observation }}</td>
                <td>{{ forfait.unit_of_measurement }}</td>
                <td>{{ formatEuro(forfait.price) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-dialog>
    </v-dialog>
    <confirm-action-dialog
      :confirm_action="confirmation_dialog"
      @change="confirmation_dialog = $event"
      @action="handleDeleteBudget()"
      :message="$capitalize($tc('model.roombudget_budget_deleted'))"
    />
  </div>
</template>

<script>
import ConfirmActionDialog from "@/components/ConfirmActionDialog";
import Money from "@/tools/money";
import {
  storeRoomBudget,
  updateRoomBudget,
  deleteRoomBudget,
  approveRoomBudget,
  calcRoomBudget
} from "@/services/budgets";

export default {
  name: "room-card",
  props: ["claim", "coverages"],
  components: {
    ConfirmActionDialog
  },
  data: () => ({
    money: {
      prefix: "€ ",
      suffix: "",
      precision: 2,
      length: 11,
      locale: "pt-PT"
    },
    forfaits_dialog: false,
    room_dialog: false,
    budget_form: {},
    total: 0,
    submitting: false,
    confirmation_dialog: false,
    budget_to_delete: null
  }),
  methods: {
    updateRoomBudgets(budgets) {
      this.$store.dispatch("damage_claim/updateBudgetsDamageClaim", {
        ...this.claim,
        budgets
      });
    },
    confirmDeleteBudget(budget) {
      this.budget_to_delete = { ...budget };
      this.confirmation_dialog = true;
    },
    async handleDeleteBudget() {
      this.submitting = true;
      try {
        await deleteRoomBudget(this.claim.id, this.budget_to_delete.id);
        this.confirmation_dialog = false;
        this.updateRoomBudgets(
          this.claim.room_budgets.filter(
            budget => budget.id != this.budget_to_delete.id
          )
        );
        this.budget_to_delete = null;
        this.confirmation_dialog = false;
      } catch (error) {
        this.$store.commit("sendMessage", {
          text: error.response.data.message
        });
      } finally {
        this.submitting = false;
      }
    },
    handleUpdateRoomBudget(budget) {
      this.budget_form = { ...budget };
      this.room_dialog = true;
    },
    async handleSelectRoomBudget(value, budget) {
      try {
        const response = await approveRoomBudget(this.claim.id, budget.id, {
          selected: value
        });
        this.updateRoomBudgets(response);
        this.$emit("submit");
      } catch (error) {
        budget.selected = !value;
      }
    },
    async handleSubmitForm() {
      const validation = await this.$refs.budget_form.validate();
      if (!validation) return;

      this.submitting = true;
      try {
        const payload = {
          ...this.budget_form,
          forfait_description: this.budget_form.forfait_description.id
            ? this.budget_form.forfait_description.description
            : this.budget_form.forfait_description
        };

        let response = [];

        if (this.budget_form.id) {
          response = await updateRoomBudget(
            this.claim.id,
            this.budget_form.id,
            payload
          );
        } else {
          response = await storeRoomBudget(this.claim.id, payload);
        }

        this.updateRoomBudgets(response);
        this.handleCloseRoomDialog();
        this.$emit("submit");
      } catch (error) {
        this.$store.commit("sendMessage", {
          text: error.response.data.message,
          color: "red"
        });
      } finally {
        this.submitting = false;
      }
    },
    test(input) {
      if (input && input.id) {
        this.budget_form = {
          ...this.budget_form,
          forfait_observation: input.observation,
          unit_of_measurement: input.unit_of_measurement,
          forfait_price: input.price,
          quantity: this.budget_form.quantity ? this.budget_form.quantity : 1
        };
      }
    },
    formatEuro(value) {
      return Money.euro(value ? value : 0);
    },
    calcBudget(budget) {
      return calcRoomBudget(budget);
    },
    calcTotalBudget() {
      return this.$listeners.calcBudget(this.claim);
    },
    handleOpenBudgetDialog() {
      this.room_dialog = true;
    },
    handleCloseRoomDialog() {
      this.budget_form = {};
      this.$refs.budget_form.reset();
      this.room_dialog = false;
    }
  },
  computed: {
    lead: function() {
      return this.$store.getters.getLead;
    },
    forfaits: function() {
      return this.$store.getters["room_forfaits/getRoomForfaits"];
    },
    budgets: function() {
      return this.claim.room_budgets ? this.claim.room_budgets : [];
    }
  }
};
</script>
